import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import {
  Alert,
  Button,
  CircularProgress,
  DialogContent,
  Stack,
  Typography,
} from '@rossum/ui/material';
import TextFieldControl from '../../../../../components/ReactHookForm/controls/TextFieldControl';
import { boldText } from '../../../../../lib/formaterValues';
import { userSelector } from '../../../../../redux/modules/user/selectors';
import { useSubmitContactDialog } from '../hooks/useSubmitContactDialog';

type ContactDialogContentProps = {
  type: 'contactUs' | 'requestAccess';
  extensionName: string;
  onClose: () => void;
};

export const ContactDialogContent = ({
  type,
  onClose,
  extensionName,
}: ContactDialogContentProps) => {
  const intl = useIntl();
  const user = useSelector(userSelector);

  const contactDialogSchema = z.object({
    email: z
      .string()
      .min(
        1,
        intl.formatMessage({
          id: 'components.contactDialog.email.error',
        })
      )
      .email(
        intl.formatMessage({
          id: 'components.contactDialog.email.error',
        })
      ),
    message: z.string().min(
      1,
      intl.formatMessage({
        id: 'components.contactDialog.message.error',
      })
    ),
  });

  const { handleSubmit, control } = useForm({
    mode: 'onSubmit',
    resolver: zodResolver(contactDialogSchema),
    defaultValues: { email: user.email ?? '', message: '' },
  });

  const { isLoading, mutate, isSuccess } = useSubmitContactDialog();

  return (
    <DialogContent sx={{ mt: 3, mx: 3, px: 0 }}>
      <Stack spacing={3}>
        {isSuccess ? (
          <Alert color="success">
            {intl.formatMessage({
              id: 'components.contactDialog.requestSent',
            })}
          </Alert>
        ) : null}

        <Typography variant="body1">
          {intl.formatMessage(
            {
              id: `components.contactDialog.subtitle.${type}`,
            },
            { boldText, name: extensionName }
          )}
        </Typography>
        <form
          onSubmit={handleSubmit(payload => {
            mutate({ payload: { ...payload, extension: extensionName } });
          })}
        >
          <Stack spacing={4}>
            <Stack spacing={2}>
              <TextFieldControl
                sx={{ width: '100%' }}
                FieldLabelProps={{ layout: 'floating' }}
                ControllerProps={{ control, name: 'email' }}
                label={intl.formatMessage({
                  id: 'components.contactDialog.email.label',
                })}
              />
              <TextFieldControl
                multiline
                sx={{ width: '100%' }}
                maxRows={8}
                type="text"
                ControllerProps={{ control, name: 'message' }}
                placeholder={intl.formatMessage({
                  id: `components.contactDialog.message.placeholder.${type}`,
                })}
              />
            </Stack>
            <Stack
              flexDirection="row"
              justifyContent="space-between"
              alignSelf="end"
            >
              <Stack spacing={1.5} direction="row" alignSelf="end">
                <Button variant="outlined" onClick={onClose} color="secondary">
                  {intl.formatMessage({
                    id: 'components.contactDialog.buttons.cancel',
                  })}
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  startIcon={
                    isLoading && <CircularProgress color="inherit" size={20} />
                  }
                  variant="contained"
                  data-cy="contact-dialog-submit-btn"
                >
                  {intl.formatMessage({
                    id: `components.contactDialog.buttons.submit.${type}`,
                  })}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </form>
      </Stack>
    </DialogContent>
  );
};
